/**
 * Conflict Detection for Appointments
 * Checks for room, clinician, and time conflicts
 */

import { createLocalDateTime, addMinutes, timePeriodsOverlap } from '../../utils/timezone';

/**
 * Check for conflicts in a single appointment or series of occurrences
 * @param {Object|Array} appointmentOrOccurrences - Single appointment or array of occurrences
 * @param {Array} existingAppointments - Existing appointments to check against
 * @param {string} excludeId - ID of appointment to exclude (when editing)
 * @returns {Object|Array} Conflict results
 */
export function checkConflicts(appointmentOrOccurrences, existingAppointments = [], excludeId = null) {
  // Handle single appointment
  if (!Array.isArray(appointmentOrOccurrences)) {
    return checkSingleAppointmentConflicts(appointmentOrOccurrences, existingAppointments, excludeId);
  }

  // Handle array of occurrences (for recurring appointments)
  const conflicts = [];
  appointmentOrOccurrences.forEach((occurrence, index) => {
    const occurrenceConflicts = checkSingleOccurrenceConflicts(
      occurrence,
      existingAppointments,
      { excludeSeriesId: excludeId }
    );

    if (occurrenceConflicts.length > 0) {
      conflicts.push({
        occurrenceIndex: index,
        occurrence,
        conflicts: occurrenceConflicts
      });
    }
  });

  return conflicts;
}

/**
 * Check conflicts for a single appointment
 * @param {Object} appointment - Single appointment
 * @param {Array} existingAppointments - Existing appointments
 * @param {string} excludeId - ID to exclude
 * @returns {Object} Conflict results
 */
function checkSingleAppointmentConflicts(appointment, existingAppointments, excludeId = null) {
  // Use timezone-aware date creation
  const appointmentStart = createLocalDateTime(appointment.date, appointment.time);
  const appointmentEnd = addMinutes(appointmentStart, appointment.duration);

  let clinicianConflict = false;
  let roomConflict = false;
  const conflicts = [];

  existingAppointments.forEach(existing => {
    // Skip the appointment being edited
    if (excludeId && existing.id === excludeId) {
      return;
    }

    // Skip cancelled appointments
    if (existing.status === 'cancelled') {
      return;
    }

    // Skip appointments with missing required data
    if (!existing.date || !existing.time || !existing.duration) {
      console.warn('checkConflicts: Skipping appointment with missing data', existing);
      return;
    }

    // Use timezone-aware date creation for existing appointment
    const existingStart = createLocalDateTime(existing.date, existing.time);
    const existingEnd = addMinutes(existingStart, existing.duration);

    // Check for time overlap using timezone-aware function
    const hasTimeOverlap = timePeriodsOverlap(
      appointmentStart, appointmentEnd,
      existingStart, existingEnd
    );

    if (hasTimeOverlap) {
      // Check room conflict
      if (appointment.roomId && existing.roomId && appointment.roomId === existing.roomId) {
        roomConflict = true;
        conflicts.push({
          type: 'room',
          resource: existing.roomId,
          conflictWith: existing,
          message: `Room conflict with appointment at ${existing.time}`
        });
      }

      // Check clinician conflict
      if (appointment.staffId && existing.staffId && appointment.staffId === existing.staffId) {
        clinicianConflict = true;
        conflicts.push({
          type: 'clinician',
          resource: existing.staffId,
          conflictWith: existing,
          message: `Clinician conflict with appointment at ${existing.time}`
        });
      }
    }
  });

  return {
    clinicianConflict,
    roomConflict,
    hasConflicts: conflicts.length > 0,
    conflicts
  };
}

/**
 * Check conflicts for a single appointment occurrence
 * @param {Object} occurrence - Single appointment occurrence
 * @param {Array} existingAppointments - Existing appointments
 * @param {Object} options - Options for conflict checking
 * @returns {Array} Array of conflicts for this occurrence
 */
function checkSingleOccurrenceConflicts(occurrence, existingAppointments, options = {}) {
  const conflicts = [];
  const { excludeSeriesId = null } = options;

  // Use timezone-aware date creation
  const occurrenceStart = createLocalDateTime(occurrence.date, occurrence.time);
  const occurrenceEnd = addMinutes(occurrenceStart, occurrence.duration);

  existingAppointments.forEach(existing => {
    // Skip appointments from the same series (when editing)
    if (excludeSeriesId && existing.seriesId === excludeSeriesId) {
      return;
    }

    // Skip cancelled appointments
    if (existing.status === 'cancelled') {
      return;
    }

    // Use timezone-aware date creation for existing appointment
    const existingStart = createLocalDateTime(existing.date, existing.time);
    const existingEnd = addMinutes(existingStart, existing.duration);

    // Check for time overlap using timezone-aware function
    const hasTimeOverlap = timePeriodsOverlap(
      occurrenceStart, occurrenceEnd,
      existingStart, existingEnd
    );

    if (hasTimeOverlap) {
      // Check room conflict
      if (occurrence.roomId && existing.roomId && occurrence.roomId === existing.roomId) {
        conflicts.push({
          type: 'room',
          resource: existing.roomId,
          conflictWith: existing,
          message: `Room conflict with appointment at ${existing.time}`
        });
      }

      // Check clinician conflict
      if (occurrence.staffId && existing.staffId && occurrence.staffId === existing.staffId) {
        conflicts.push({
          type: 'clinician',
          resource: existing.staffId,
          conflictWith: existing,
          message: `Clinician conflict with appointment at ${existing.time}`
        });
      }
    }
  });

  return conflicts;
}

/**
 * Get conflict summary for display
 * @param {Array} conflicts - Array of conflicts from checkConflicts
 * @returns {Object} Conflict summary
 */
export function getConflictSummary(conflicts) {
  const totalConflicts = conflicts.reduce((sum, item) => sum + item.conflicts.length, 0);
  const roomConflicts = conflicts.reduce((sum, item) =>
    sum + item.conflicts.filter(c => c.type === 'room').length, 0
  );
  const clinicianConflicts = conflicts.reduce((sum, item) =>
    sum + item.conflicts.filter(c => c.type === 'clinician').length, 0
  );

  return {
    total: totalConflicts,
    room: roomConflicts,
    clinician: clinicianConflicts,
    hasConflicts: totalConflicts > 0
  };
}

/**
 * Format conflict message for display
 * @param {Object} conflict - Single conflict object
 * @returns {string} Formatted message
 */
export function formatConflictMessage(conflict) {
  const { type, conflictWith } = conflict;
  const time = conflictWith.time;
  const date = new Date(conflictWith.date).toLocaleDateString();

  if (type === 'room') {
    return `Room conflict at ${time} on ${date}`;
  } else if (type === 'clinician') {
    return `Clinician conflict at ${time} on ${date}`;
  }

  return conflict.message || 'Scheduling conflict detected';
}
